import { Download, Kanban, LayoutList, Loader2, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { DateRangeFilter } from './DateRangeFilter';
import { MultiSelectDropdown } from './MultiSelectDropdown';

export interface JobPostingOption {
  id: string;
  label: string;
}

interface FilterBarProps {
  searchInput: string;
  onSearchChange: (value: string) => void;
  roles: string[];
  allRoles: string[];
  onRolesChange: (values: string[]) => void;
  sites: string[];
  allSites: string[];
  onSitesChange: (values: string[]) => void;
  stages: string[];
  allStages: string[];
  onStagesChange: (values: string[]) => void;
  renderStage: (stage: string) => string;
  jobPostingId?: string;
  jobPostingOptions?: JobPostingOption[];
  onJobPostingChange?: (value: string) => void;
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  activeFilterCount: number;
  onClearAll: () => void;
  onExport?: () => void;
  isExporting?: boolean;
  viewMode?: 'table' | 'kanban';
  onViewModeChange?: (mode: 'table' | 'kanban') => void;
}

export function FilterBar({
  searchInput,
  onSearchChange,
  roles,
  allRoles,
  onRolesChange,
  sites,
  allSites,
  onSitesChange,
  stages,
  allStages,
  onStagesChange,
  renderStage,
  jobPostingId,
  jobPostingOptions,
  onJobPostingChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  activeFilterCount,
  onClearAll,
  onExport,
  isExporting = false,
  viewMode = 'table',
  onViewModeChange,
}: FilterBarProps) {
  const { t } = useTranslation();

  return (
    <div className='space-y-3'>
      <div className='flex flex-col md:flex-row gap-3'>
        <div className='relative flex-1'>
          <Search className='absolute left-3 top-2.5 h-4 w-4 text-gray-400' />
          <input
            type='text'
            placeholder={t('submissions.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            className='w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:ring-primary focus:border-primary bg-white text-gray-900'
          />
        </div>

        <div className='flex flex-wrap gap-2'>
          <MultiSelectDropdown
            label={t('submissions.filters.roles')}
            options={allRoles}
            selected={roles}
            onChange={onRolesChange}
          />
          <MultiSelectDropdown
            label={t('submissions.filters.sites')}
            options={allSites}
            selected={sites}
            onChange={onSitesChange}
          />
          <MultiSelectDropdown
            label={t('submissions.filters.stages')}
            options={allStages}
            selected={stages}
            onChange={onStagesChange}
            renderOption={renderStage}
          />
        </div>
      </div>

      <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-3'>
        <div className='flex flex-wrap items-center gap-3'>
          <DateRangeFilter
            startDate={dateFrom}
            endDate={dateTo}
            onStartDateChange={onDateFromChange}
            onEndDateChange={onDateToChange}
          />

          {jobPostingOptions && onJobPostingChange && (
            <select
              value={jobPostingId ?? ''}
              onChange={(e) => onJobPostingChange(e.target.value)}
              className='max-w-[280px] truncate rounded-md border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-gray-700 focus:border-primary focus:ring-1 focus:ring-primary outline-none'>
              <option value=''>{t('submissions.filters.allJobPostings')}</option>
              {jobPostingOptions.map((jp) => (
                <option key={jp.id} value={jp.id}>
                  {jp.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className='flex items-center gap-2'>
          {onViewModeChange && (
            <div className='inline-flex rounded-md border border-gray-300 bg-white'>
              <button
                type='button'
                onClick={() => onViewModeChange('table')}
                className={cn(
                  'inline-flex items-center px-2 py-1.5 rounded-l-md text-sm transition-colors',
                  viewMode === 'table'
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
                )}
                title={t('submissions.viewTable')}>
                <LayoutList size={16} />
              </button>
              <button
                type='button'
                onClick={() => onViewModeChange('kanban')}
                className={cn(
                  'inline-flex items-center px-2 py-1.5 rounded-r-md text-sm transition-colors border-l border-gray-300',
                  viewMode === 'kanban'
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
                )}
                title={t('submissions.viewKanban')}>
                <Kanban size={16} />
              </button>
            </div>
          )}

          {activeFilterCount > 0 && (
            <button
              type='button'
              onClick={onClearAll}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium',
                'text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors',
              )}>
              <X size={14} />
              {t('submissions.filters.clearAll')}
              <span className='inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-gray-200 text-xs font-medium'>
                {activeFilterCount}
              </span>
            </button>
          )}

          {onExport && (
            <button
              type='button'
              onClick={onExport}
              disabled={isExporting}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium',
                'text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}>
              {isExporting ? <Loader2 size={14} className='animate-spin' /> : <Download size={14} />}
              {isExporting ? t('submissions.exporting') : t('submissions.export')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
